import { Icon } from "@fluentui/react";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { t } from "i18next";

/**
 * The row mechanics both levels of the board share: dragging by a grip, and a
 * line that opens what it names.
 *
 * Held here rather than in either table because the group table and the protocol
 * sub-table are the same table one rank apart, and a rank is a matter of size and
 * tone — not of how a row is picked up or pressed. Putting them in one of the two
 * would also make the pair import each other.
 */

/**
 * Whether a press belongs to the row or to a control sitting on it.
 *
 * The line is a click target and also carries a grip, a chevron and the buttons of
 * the action column. One test against what a control is made of settles all of
 * them at once, and any added later with them — no control has to remember to stop
 * the event on its way up, which is the kind of thing one of them eventually
 * forgets.
 */
const ROW_CONTROL = ["button", "a", "input", "select", "textarea", "label"].join(", ");

const pressedTheRow = (event) =>
  !(event.target instanceof Element) || !event.target.closest(ROW_CONTROL);

/** Click and keyboard for a line that opens what it names. */
export const openHandlers = (open) => ({
  onClick: (event) => {
    if (pressedTheRow(event)) open();
  },
  onKeyDown: (event) => {
    // Only the row itself: enter on a button inside it is that button's press.
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    open();
  },
});

/**
 * The sensors a level of the board drags with.
 *
 * A short pointer threshold: the grip does nothing but drag, so there is no click
 * of its own to protect, and a longer one only makes the drag feel unresponsive.
 * The keyboard sensor is what makes reordering reachable without a mouse, through
 * the grip button below.
 */
export const useBoardSensors = () =>
  useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

/** The drag context a whole level of rows shares, so a row can only be dropped among its peers. */
export function BoardSortable({ ids, onDragEnd, children }) {
  const sensors = useBoardSensors();

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/**
 * A row that can be picked up by the grip it hands back.
 *
 * The grip goes to the caller rather than being rendered here because it belongs
 * inside the row's own line, while the element that actually moves may be more
 * than that line — a group card is its line plus the drawer under it.
 *
 * `gripClassName` is how a rank asks for a smaller grip: the sub-table's rows are
 * shorter than the group lines, and a grip sized for the taller row would set the
 * height of the shorter one.
 */
export function SortableRow({ id, disabled, className, gripLabel, gripClassName = "", children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });

  const grip = disabled ? (
    <span className={`hpv2-board-grip-placeholder ${gripClassName}`} />
  ) : (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className={`hpv2-board-grip ${gripClassName}`}
      title={t("hanging-protocol.drag-to-reorder")}
      aria-label={gripLabel}
      {...attributes}
      {...listeners}
    >
      <Icon iconName="GripperDotsVertical" />
    </button>
  );

  return (
    <li
      ref={setNodeRef}
      className={`${className} ${isDragging ? "is-dragging" : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // A row in flight has to clear the ones it passes over.
        zIndex: isDragging ? 2 : undefined,
      }}
    >
      {children(grip)}
    </li>
  );
}
